import { DatePicker } from "@app/components/DatePicker.tsx";
import Kvp from "@app/components/kvp.tsx";
import SaveAndCloseOrAddButton from "@app/components/save-and-close-or-add-button.tsx";
import { ActiveEnums } from "@app/data/engine.ts";
import useAssignmentData from "@app/data-hooks/use-assignment-data.ts";
import useCategoryData from "@app/data-hooks/use-category-data.ts";
import useEntriesData from "@app/data-hooks/use-entries-data.ts";
import { useExpenseDetailData } from "@app/data-hooks/use-expense-detail-data.ts";
import useStaffData from "@app/data-hooks/use-staff-data.ts";
import useWorkLocationData from "@app/data-hooks/use-work-location-data.ts";
import useWorkPackageData from "@app/data-hooks/use-work-package-data.ts";
import { formatUsd } from "@app/helpers/money-helper.ts";
import { formatDate } from "@app/helpers/time-helper.ts";
import useAuth from "@app/hooks/use-auth.ts";
import { entryOrderStore } from "@app/stores/entry-store.ts";
import { Box, Button, Checkbox, FormControlLabel, Grid2, Stack, TextField, Typography } from "@mui/material";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { ConfirmDialog, CurrencyInput, QueryStatusRouter, Select } from "bi-lib";
import moment, { Moment } from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

interface FormProps {
    assignmentId?: number;
    billable?: boolean;
    costAmount?: number;
    description?: string;
    fromDate?: Moment;
    toDate?: Moment;
    invoiceVisible?: boolean;
    quantity?: number;
    reimbursable?: boolean;
    staffId?: number;
    workPackageId?: number;
    subcategoryId?: number;
    phaseId?: number;
    workLocationId?: number;
}

interface Props {
    id?: string;
    catId: number;
    from: string;
}

export default function EntryForm({ id, catId, from }: Props) {
    const [formData, setFormData] = useState<FormProps>({});
    const [costAmountUpdateQueued, setCostAmountUpdateQueued] = useState(false);
    const [errors, setErrors] = useState<{ quantity?: string }>({});

    const { userData, isAdmin } = useAuth();

    const queuedAction = useRef<"add" | "close" | null>(null);

    const [endEnabled, setEndEnabled] = useState(false);

    const { entry, updateEntry } = useEntriesData({ id });
    const { categories, subcategoryList } = useCategoryData({ entryCategoryId: entry.data?.category ?? catId });
    const entryOrder = useStore(entryOrderStore);
    const { next: nextEntry, previous: previousEntry } = useMemo(() => {
        if (!entryOrder || id === undefined || !entryOrder.includes(id)) {
            return { next: undefined, previous: undefined };
        }
        const index = entryOrder.indexOf(id);
        const next = index < entryOrder.length - 1 ? entryOrder[index + 1] : undefined;
        const previous = index > 0 ? entryOrder[index - 1] : undefined;
        return { next, previous };
    }, [entryOrder, id]);

    const navigate = useNavigate();
    const location = useLocation();

    const handleSubmitForm = async () => {
        if (entry.data?.isPosted) {
            if (entry.data?.isPosted) {
                setConfirm(() => () => handleSubmit(formData));
                return;
            }
        }
        await handleSubmit(formData);
        if (id == undefined) {
            void navigate({ to: from });
        }
    };

    // Reset form with new entry data when entry changes
    useEffect(() => {
        if (!entry.data) {
            setFormData({
                staffId: userData?.UserId,
                fromDate: moment(),
            });
            return;
        }

        setFormData({
            assignmentId: entry.data?.assignmentId,
            billable: entry.data?.isBillable,
            costAmount: entry.data?.costAmount,
            description: entry.data?.description ?? undefined,
            fromDate: moment(entry.data?.itemDate),
            invoiceVisible: entry.data?.isInvoiceVisible,
            quantity: entry.data?.quantity,
            reimbursable: entry.data?.isReimbursable,
            staffId: entry.data?.staffId ?? userData?.UserId,
            workPackageId: entry.data?.workPackageId,
            subcategoryId: entry.data?.subcategoryId ?? (catId == 1 ? 1 : undefined),
            phaseId: entry.data?.phaseId,
            workLocationId: entry.data?.workLocationId,
        });
    }, [userData?.UserId, entry.data, catId]);

    const showQuantity = useMemo(
        () =>
            (entry.data?.category ?? catId) == 1 ||
            subcategoryList.data?.find((x) => x.subcategoryId == formData.subcategoryId)?.quantityRate,
        [entry.data?.category, subcategoryList.data, formData.subcategoryId, catId],
    );

    const { staffingList, staffAssignmentsList } = useStaffData({
        staffIds: formData.staffId?.toString(),
        activeState: ActiveEnums.ActiveOnly,
    });
    const { workLocationsList } = useWorkLocationData({ assignmentIds: formData.assignmentId?.toString() });
    const { assignmentPhaseList, assignmentDetailSearch, wpCodeRequired } = useAssignmentData({
        assignmentIds: formData.assignmentId?.toString(),
        assignmentDetailId: formData.assignmentId,
        staffId: formData.staffId,
        workPackageId: formData.workPackageId,
        assignmentDt: formData.fromDate,
    });
    const { workPackagesListByAssignment } = useWorkPackageData({
        assignmentId: formData.assignmentId,
        phaseId: formData.phaseId,
    });
    const { costRate } = useExpenseDetailData({
        assignmentId: formData.assignmentId,
        subCategoryId: formData.subcategoryId,
        assignmentDt: formData.fromDate == undefined ? undefined : (formatDate(formData.fromDate) ?? undefined),
    });

    const [confirm, setConfirm] = useState<() => void>();

    useEffect(() => {
        if (assignmentDetailSearch.data == undefined || id != undefined) {
            return;
        }

        setFormData((prev) => ({ ...prev, billable: assignmentDetailSearch.data.billableTime }));
        let invoiceVisible: boolean | undefined;
        switch (catId) {
            case 1:
                invoiceVisible = assignmentDetailSearch.data.invoiceVisibleTime;
                break;
            case 2:
                invoiceVisible = assignmentDetailSearch.data.invoiceVisibleTravelExpense;
                break;
            case 3:
                invoiceVisible = assignmentDetailSearch.data.invoiceVisibleOtherIndividualExpense;
                break;
            case 4:
                invoiceVisible = assignmentDetailSearch.data.invoiceVisibleOfficeExpense;
                break;
        }
        setFormData((prev) => ({
            ...prev,
            invoiceVisible,
            reimbursable: assignmentDetailSearch.data.timeReimbursable,
        }));
    }, [assignmentDetailSearch.data, id, catId]);

    useEffect(() => {
        if (staffAssignmentsList.data?.length != 1) {
            return;
        }
        const data = staffAssignmentsList.data[0].assignmentId;
        setFormData((prev) => ({ ...prev, assignmentId: data }));
        return;
    }, [staffAssignmentsList.data]);

    useEffect(() => {
        if (assignmentPhaseList.data?.length != 1) {
            return;
        }
        const data = assignmentPhaseList.data[0].assignmentPhaseId;
        setFormData((prev) => ({ ...prev, phaseId: data }));
        return;
    }, [assignmentPhaseList.data]);

    useEffect(() => {
        if (workLocationsList.data?.length != 1) {
            return;
        }
        const data = workLocationsList.data[0].locationId;
        setFormData((prev) => ({ ...prev, workLocationId: data }));
        return;
    }, [workLocationsList.data]);

    useEffect(() => {
        if (workPackagesListByAssignment.data?.length != 1) {
            return;
        }
        const data = workPackagesListByAssignment.data[0].workPackageId;
        setFormData((prev) => ({ ...prev, workPackageId: data }));
        return;
    }, [workPackagesListByAssignment.data]);

    useEffect(() => {
        if (!showQuantity || !costAmountUpdateQueued) {
            return;
        }
        setFormData((prev) => ({ ...prev, costAmount: (prev.quantity ?? 0) * (costRate.data?.costRate ?? 0) }));
        setCostAmountUpdateQueued(false);
    }, [costRate.data, formData.quantity, showQuantity, costAmountUpdateQueued]);

    useEffect(() => {
        if (subcategoryList.data == undefined || catId != 1) {
            return;
        }
        if (subcategoryList.data.length != 1) {
            console.error(`Unexpected number of time subcategories: ${subcategoryList.data.length}`);
            toast.error("An unexpected error occurred.");
            return;
        }
        setFormData((prev) => ({ ...prev, subcategoryId: subcategoryList.data[0].subcategoryId }));
    }, [subcategoryList.data, catId]);

    const handleSubmit = async (value: FormProps) => {
        await updateEntry.mutateAsync({
            entryId: id,
            assignmentId: value.assignmentId,
            billable: value.billable,
            categoryId: entry.data?.category ?? catId,
            costAmount: value.costAmount,
            description: value.description,
            fromDate: value.fromDate?.format("MM/DD/YYYY"),
            toDate: value.toDate?.format("MM/DD/YYYY"),
            invoiceVisible: value.invoiceVisible,
            isPosted: entry.data?.isPosted,
            phaseId: formData.phaseId,
            quantity: value.quantity,
            reimbursable: value.reimbursable,
            staffId: value.staffId,
            subCategoryId: value.subcategoryId,
            workLocationId: value.workLocationId,
            workPackageId: value.workPackageId,
        });

        if (queuedAction.current == "add") {
            void navigate({ to: "/entry/create", search: { catId, from: location.href } });
        } else if (queuedAction.current == "close") {
            void navigate({ to: from });
        }
    };

    const formRef = useRef<HTMLFormElement>(null);

    return (
        <>
            <ConfirmDialog
                onConfirm={() => confirm?.()}
                onClose={() => setConfirm(undefined)}
                open={confirm != undefined}
            >
                <Typography>This entry has been posted. Are you sure you want to edit it?</Typography>
            </ConfirmDialog>
            <form ref={formRef}>
                <QueryStatusRouter queryHook={entry}>
                    <Grid2 container spacing={1} mt={2}>
                        <Grid2 size={6}>
                            <Stack spacing={1}>
                                <QueryStatusRouter queryHook={categories}>
                                    <Select
                                        label={"Category"}
                                        value={entry.data?.category ?? catId}
                                        required
                                        disabled
                                        fullWidth
                                        onChange={() => {}}
                                        options={categories.data?.map((x) => ({
                                            label: x.categoryDescription,
                                            value: x.categoryId,
                                        }))}
                                    />
                                </QueryStatusRouter>
                                <DatePicker
                                    label={`${endEnabled ? "Start " : ""}Date`}
                                    value={formData.fromDate}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, fromDate: e ?? prev.fromDate }))}
                                    sx={{ width: "100%" }}
                                    required
                                />
                                {id == undefined && (
                                    <Box display={"flex"} alignItems={"center"}>
                                        <Box pr={1}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={endEnabled}
                                                        onChange={(e) => setEndEnabled(e.target.checked)}
                                                    />
                                                }
                                                label={"Has End"}
                                            />
                                        </Box>
                                        <DatePicker
                                            disabled={!endEnabled}
                                            label={"End Date"}
                                            value={formData.toDate}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, toDate: e ?? prev.toDate }))
                                            }
                                            sx={{ flexGrow: 1 }}
                                            required={endEnabled}
                                        />
                                    </Box>
                                )}
                                <QueryStatusRouter queryHook={workLocationsList}>
                                    <Select
                                        label={"Location"}
                                        value={formData.workLocationId}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                workLocationId: e,
                                                workPackageId: undefined,
                                            }))
                                        }
                                        disabled={
                                            formData.assignmentId == undefined || workLocationsList.data?.length == 1
                                        }
                                        fullWidth
                                        options={workLocationsList.data?.map((x) => ({
                                            label: x.displayName,
                                            value: x.locationId,
                                        }))}
                                        required
                                    />
                                </QueryStatusRouter>
                                {catId == 1 && (
                                    <QueryStatusRouter queryHook={workPackagesListByAssignment}>
                                        <Select
                                            required={wpCodeRequired.data}
                                            label={"Work Package"}
                                            value={formData.workPackageId}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, workPackageId: e }))}
                                            disabled={
                                                formData.assignmentId == undefined || formData.phaseId == undefined
                                            }
                                            fullWidth
                                            options={workPackagesListByAssignment.data?.map((x) => ({
                                                label: x.displayName,
                                                value: x.workPackageId,
                                            }))}
                                        />
                                    </QueryStatusRouter>
                                )}
                                {(entry.data?.category ?? catId) != 1 && (
                                    <QueryStatusRouter queryHook={subcategoryList}>
                                        <Select
                                            label={"Expense Type"}
                                            value={formData.subcategoryId}
                                            onChange={(e) => {
                                                setFormData((prev) => ({ ...prev, subcategoryId: e }));
                                                setCostAmountUpdateQueued(true);
                                            }}
                                            options={subcategoryList.data?.map((x) => ({
                                                label: x.subcategoryDescription,
                                                value: x.subcategoryId,
                                            }))}
                                            disabled={formData.assignmentId == undefined}
                                        />
                                    </QueryStatusRouter>
                                )}
                            </Stack>
                        </Grid2>
                        <Grid2 size={6}>
                            <Stack spacing={1}>
                                <QueryStatusRouter queryHook={staffingList}>
                                    <Select
                                        label={"Staff"}
                                        value={formData.staffId}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                staffId: e,
                                                assignmentId: undefined,
                                                workLocationId: undefined,
                                                workPackageId: undefined,
                                            }))
                                        }
                                        required
                                        fullWidth
                                        options={(staffingList.data == undefined ||
                                        staffingList.data.some((x) => x.staffingId == entry.data?.staffId)
                                            ? staffingList.data
                                            : [
                                                  ...staffingList.data,
                                                  ...(entry.data == undefined
                                                      ? []
                                                      : [
                                                            {
                                                                staffingId: entry.data?.staffId,
                                                                lastName: entry.data?.lastName,
                                                                firstName: entry.data?.firstName,
                                                                isActive: false,
                                                            },
                                                        ]),
                                              ]
                                        )?.map((x) => ({
                                            label: (
                                                <Box
                                                    display={"flex"}
                                                    justifyContent={"space-between"}
                                                    alignItems={"center"}
                                                >
                                                    <Typography>
                                                        {x.lastName}, {x.firstName}
                                                    </Typography>
                                                    {!x.isActive && (
                                                        <Typography fontStyle={"italic"}>(Inactive)</Typography>
                                                    )}
                                                </Box>
                                            ),
                                            value: x.staffingId,
                                        }))}
                                    />
                                </QueryStatusRouter>
                                <QueryStatusRouter queryHook={staffAssignmentsList}>
                                    <Select
                                        label={"Assignment"}
                                        value={formData.assignmentId}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                assignmentId: e,
                                                workLocationId: undefined,
                                                workPackageId: undefined,
                                            }))
                                        }
                                        required
                                        fullWidth
                                        options={staffAssignmentsList.data?.map((x) => ({
                                            label: x.displayName,
                                            value: x.assignmentId,
                                        }))}
                                    />
                                </QueryStatusRouter>
                                {catId == 1 && (
                                    <QueryStatusRouter queryHook={assignmentPhaseList}>
                                        <Select
                                            label={"Phase"}
                                            value={formData.phaseId}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, phaseId: e }))}
                                            disabled={
                                                formData.assignmentId == undefined ||
                                                assignmentPhaseList.data?.length == 1
                                            }
                                            fullWidth
                                            options={assignmentPhaseList.data?.map((x) => ({
                                                label: x.displayName,
                                                value: x.assignmentPhaseId,
                                            }))}
                                        />
                                    </QueryStatusRouter>
                                )}
                                <TextField
                                    label={"Description"}
                                    value={formData.description ?? ""}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                                    required
                                    multiline
                                    minRows={4}
                                    fullWidth
                                />
                            </Stack>
                        </Grid2>
                        {showQuantity && (
                            <Grid2 size={4}>
                                <TextField
                                    type={"number"}
                                    label={(entry.data?.category ?? catId) == 1 ? "Hours" : "Quantity"}
                                    value={formData.quantity ?? ""}
                                    onChange={(e) => {
                                        const value = +e.target.value;
                                        setFormData((prev) => ({
                                            ...prev,
                                            quantity: value,
                                        }));
                                        setCostAmountUpdateQueued(true);
                                    }}
                                    required
                                    onBlur={(e) => {
                                        const value = +e.target.value;
                                        if (value <= 0) {
                                            setErrors((prev) => ({
                                                ...prev,
                                                quantity: "Value must be greater than 0",
                                            }));
                                            return;
                                        }
                                        setErrors((prev) => ({ ...prev, quantity: undefined }));
                                    }}
                                    helperText={errors.quantity}
                                    error={errors.quantity != undefined}
                                    fullWidth
                                />
                            </Grid2>
                        )}
                        {catId != 1 && (
                            <>
                                {showQuantity && (
                                    <Grid2 size={4} alignContent={"center"}>
                                        <QueryStatusRouter queryHook={costRate}>
                                            <Kvp keyTitle={"Rate"}>
                                                <Typography variant={"body2"}>
                                                    {formatUsd(costRate.data?.costRate)}/unit
                                                </Typography>
                                            </Kvp>
                                        </QueryStatusRouter>
                                    </Grid2>
                                )}
                                <Grid2 size={4}>
                                    <QueryStatusRouter queryHook={[entry, subcategoryList, costRate]}>
                                        <CurrencyInput
                                            type={"number"}
                                            label={"Cost Amount"}
                                            value={formData.costAmount ?? ""}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, costAmount: e }))}
                                            required
                                            helperText={
                                                (formData.costAmount ?? 0) <= 0 ? "Value must be greater than 0" : ""
                                            }
                                            error={(formData.costAmount ?? 0) <= 0}
                                            fullWidth
                                        />
                                    </QueryStatusRouter>
                                </Grid2>
                            </>
                        )}
                        {catId == 1 && (
                            <>
                                <Grid2 size={12}>
                                    <Kvp keyTitle={"Cost"}>
                                        {formatUsd(
                                            (formData.quantity ?? 0) * (assignmentDetailSearch.data?.costRate ?? 0),
                                        )}
                                    </Kvp>
                                    {isAdmin && (
                                        <>
                                            <Kvp keyTitle={"Billing"}>
                                                {formatUsd(
                                                    (formData.quantity ?? 0) *
                                                        (assignmentDetailSearch.data?.billingRate ?? 0),
                                                )}
                                            </Kvp>
                                            <Kvp keyTitle={"Internal"}>
                                                {formatUsd(
                                                    (formData.quantity ?? 0) *
                                                        (assignmentDetailSearch.data?.internalRate ?? 0),
                                                )}
                                            </Kvp>
                                        </>
                                    )}
                                </Grid2>
                            </>
                        )}
                        <Grid2 size={12}>
                            <Box display={"flex"} justifyContent={"space-between"}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.billable ?? false}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, billable: e.target.checked }))
                                            }
                                        />
                                    }
                                    label={"Billable"}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.invoiceVisible ?? false}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, invoiceVisible: e.target.checked }))
                                            }
                                        />
                                    }
                                    label={"Invoice Visible"}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.reimbursable ?? false}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, reimbursable: e.target.checked }))
                                            }
                                        />
                                    }
                                    label={"Reimbursable"}
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={entry.data?.isPosted ?? false} />}
                                    label={"Posted"}
                                    disabled
                                />
                            </Box>
                        </Grid2>
                    </Grid2>
                </QueryStatusRouter>
            </form>
            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} gap={1} width={"100%"} mt={2}>
                <Box display={"flex"} gap={1}>
                    <Link to={from}>
                        <Button variant={"outlined"}>Return to List</Button>
                    </Link>
                    <SaveAndCloseOrAddButton
                        onSave={handleSubmitForm}
                        onSecondary={
                            id == undefined
                                ? () => {
                                      if (entry.data?.isPosted) {
                                          queuedAction.current = "add";
                                          return;
                                      }
                                      void navigate({ to: "/entry/create", search: { catId, from } });
                                  }
                                : () => {
                                      if (entry.data?.isPosted) {
                                          queuedAction.current = "close";
                                          return;
                                      }
                                      void navigate({ to: from });
                                  }
                        }
                        primaryActionTitle={id == undefined ? "Close" : undefined}
                        secondaryActionTitle={id == undefined ? "Add New" : "Close"}
                        formRef={formRef}
                    />
                </Box>
                <Box display={"flex"} gap={1}>
                    {id != undefined && (
                        <>
                            {previousEntry != undefined && (
                                <Link to={"/entry/$id"} params={{ id: previousEntry }} search={{ catId, from }}>
                                    <Button variant={"outlined"}>Previous</Button>
                                </Link>
                            )}
                            {nextEntry != undefined && (
                                <Link to={"/entry/$id"} params={{ id: nextEntry }} search={{ catId, from }}>
                                    <Button variant={"outlined"}>Next</Button>
                                </Link>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </>
    );
}
